"use client";

import { useLocale } from "next-intl";
import { useEffect, useRef } from "react";
import { useProject, useProjectConfigs } from "@/features/project/ProjectContext";
import { useModalStore } from "@/hooks/useModal";
import {
  APP_INFO_MODAL_ID,
  pagesFor,
  showsAppInfo,
  showsTutorialOnLoad,
  TUTORIAL_ID,
  visitedKey,
} from "./appInfo";

/**
 * Decide, once per page load, whether the visitor is shown the project's
 * introduction or the first-run tutorial.
 *
 * The decision is made here rather than inside either overlay because it is one
 * decision: both take the whole screen, so at most one of them can win, and
 * whichever runs also marks the project as visited.
 *
 * `localStorage` is read in an effect rather than during render — this runs on
 * the first paint of the map, and reading storage while rendering makes the
 * result depend on when React happens to call the component.
 */
export function useOnboarding() {
  const configs = useProjectConfigs();
  const locale = useLocale();
  const open = useModalStore((state) => state.open);

  // The project's own domain, not `configs.domain`: the sync doesn't put one in
  // the config, so gating on that meant neither overlay ever ran.
  const domain = useProject()?.domain;

  const {
    appInfoModal,
    appInfoModalEnabled,
    appInfoModalEmergence,
    tutorialEnabledOnFirstLoad,
  } = configs;

  // Once per mount: `onEveryLoad` means every page load, not every re-render.
  const decided = useRef(false);

  useEffect(() => {
    if (decided.current || !domain) return;
    decided.current = true;

    const key = visitedKey(domain);
    const isFirstVisit = !localStorage.getItem(key);
    if (isFirstVisit) localStorage.setItem(key, "1");

    if (
      showsAppInfo({
        enabled: appInfoModalEnabled,
        emergence: appInfoModalEmergence,
        pageCount: pagesFor(appInfoModal, locale).length,
        isFirstVisit,
      })
    ) {
      open(APP_INFO_MODAL_ID);
      return;
    }

    if (
      showsTutorialOnLoad({
        appInfoEnabled: appInfoModalEnabled,
        tutorialOnFirstLoad: tutorialEnabledOnFirstLoad,
        isFirstVisit,
      })
    ) {
      open(TUTORIAL_ID);
    }
  }, [
    domain,
    locale,
    appInfoModal,
    appInfoModalEnabled,
    appInfoModalEmergence,
    tutorialEnabledOnFirstLoad,
    open,
  ]);
}
